import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Wraps a horizontally-scrollable element and renders a proxy scrollbar
 * pinned to the bottom of the viewport whenever the wrapper is on-screen.
 *
 * Useful for very tall tables where the native scrollbar would otherwise
 * sit far below the fold and force the user to scroll the page down
 * before they can scroll the table sideways.
 *
 * Implementation notes:
 *  - The proxy bar uses `position: fixed` (not `sticky`) to avoid the
 *    well-known containing-block pitfalls when an ancestor uses
 *    `overflow: hidden` on either axis.
 *  - It only renders when the wrapper is partially in the viewport AND
 *    the inner native scrollbar is currently below the fold. Otherwise
 *    the native bar is good enough.
 *  - Both scroll containers are kept in sync via direct DOM writes so
 *    React doesn't re-render on every wheel tick.
 */
export default function StickyHorizontalScroll({ children, className = "", innerClassName = "" }) {
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);
  const proxyRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [proxyLeft, setProxyLeft] = useState(0);
  const [proxyWidth, setProxyWidth] = useState(0);
  const [needsProxy, setNeedsProxy] = useState(false);
  const [showProxy, setShowProxy] = useState(false);
  const syncingFromProxy = useRef(false);
  const syncingFromMain = useRef(false);

  // Track inner content width and whether horizontal overflow exists.
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;

    const update = () => {
      const sw = node.scrollWidth;
      const cw = node.clientWidth;
      setContentWidth(sw);
      setNeedsProxy(sw > cw + 1);
      const rect = node.getBoundingClientRect();
      setProxyLeft(rect.left);
      setProxyWidth(rect.width);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(node);
    if (node.firstElementChild) ro.observe(node.firstElementChild);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
    };
  }, [children]);

  // Show the proxy only when the native scrollbar at the table's bottom
  // is below the viewport (i.e. user can't reach it without scrolling).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    const evaluate = () => {
      if (!needsProxy) {
        setShowProxy(false);
        return;
      }
      const rect = wrapper.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      // Wrapper is in view AND its bottom edge is below the viewport.
      const inView = rect.bottom > 0 && rect.top < viewportH;
      const bottomBelowFold = rect.bottom > viewportH;
      setShowProxy(inView && bottomBelowFold);
    };

    evaluate();
    window.addEventListener("scroll", evaluate, { passive: true });
    window.addEventListener("resize", evaluate);
    return () => {
      window.removeEventListener("scroll", evaluate);
      window.removeEventListener("resize", evaluate);
    };
  }, [needsProxy]);

  // Sync proxy → main.
  const handleProxyScroll = useCallback(() => {
    if (syncingFromMain.current) {
      syncingFromMain.current = false;
      return;
    }
    const main = scrollRef.current;
    const proxy = proxyRef.current;
    if (!main || !proxy) return;
    if (main.scrollLeft !== proxy.scrollLeft) {
      syncingFromProxy.current = true;
      main.scrollLeft = proxy.scrollLeft;
    }
  }, []);

  // Sync main → proxy.
  const handleMainScroll = useCallback(() => {
    if (syncingFromProxy.current) {
      syncingFromProxy.current = false;
      return;
    }
    const main = scrollRef.current;
    const proxy = proxyRef.current;
    if (!main || !proxy) return;
    if (proxy.scrollLeft !== main.scrollLeft) {
      syncingFromMain.current = true;
      proxy.scrollLeft = main.scrollLeft;
    }
  }, []);

  useEffect(() => {
    const main = scrollRef.current;
    if (!main) return undefined;
    main.addEventListener("scroll", handleMainScroll, { passive: true });
    return () => main.removeEventListener("scroll", handleMainScroll);
  }, [handleMainScroll]);

  // Push the proxy's scrollLeft to match the main on first show.
  useEffect(() => {
    if (!showProxy) return;
    const main = scrollRef.current;
    const proxy = proxyRef.current;
    if (main && proxy && proxy.scrollLeft !== main.scrollLeft) {
      syncingFromMain.current = true;
      proxy.scrollLeft = main.scrollLeft;
    }
  }, [showProxy]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div ref={scrollRef} className={`overflow-x-auto ${innerClassName}`}>
        {children}
      </div>
      {showProxy && (
        <div
          ref={proxyRef}
          onScroll={handleProxyScroll}
          aria-hidden="true"
          className="sticky-h-scroll-proxy overflow-x-scroll bg-white/95 backdrop-blur-sm border-t border-palette-slate-divider shadow-[0_-2px_6px_rgba(0,0,0,0.08)]"
          style={{
            position: "fixed",
            left: proxyLeft,
            width: proxyWidth,
            bottom: 0,
            height: 26,
            paddingTop: 6,
            paddingBottom: 6,
            zIndex: 30,
          }}
        >
          <div style={{ width: contentWidth, height: 1 }} />
        </div>
      )}
    </div>
  );
}
